import React from 'react';
import { Check, X, Crown } from 'lucide-react';
import { SectionHead, Reveal, SCROLL_MT } from './shared';

function Cell({ value, last }) {
  if (typeof value === 'string' && value.trim()) {
    return <span className={`text-[12px] sm:text-[13px] font-semibold leading-snug ${last ? 'text-emerald-700' : 'text-slate-500'}`}>{value}</span>;
  }
  return value
    ? <span className={`w-7 h-7 rounded-full flex items-center justify-center ${last ? 'bg-emerald-500 text-white shadow-[0_8px_18px_-8px_rgba(16,185,129,0.8)]' : 'bg-emerald-100 text-emerald-600'}`}><Check size={15} strokeWidth={3} /></span>
    : <span className="w-7 h-7 rounded-full bg-neutral-100 text-neutral-400 flex items-center justify-center"><X size={14} strokeWidth={2.5} /></span>;
}

export default function Compare({ compare }) {
  const columns = (compare.columns || []).filter(Boolean);
  const rows = (compare.rows || []).filter(r => r && r.label);
  if (!columns.length || !rows.length) return null;
  const lastIdx = columns.length - 1;
  const gridCols = `minmax(0,1.6fr) repeat(${columns.length}, minmax(64px, 0.7fr))`;

  return (
    <section id="why" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20 bg-white/40 border-y border-white`} data-testid="compare-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <SectionHead label={compare.label} title={compare.title} subtitle={compare.subtitle} className="mb-8 sm:mb-10" />
        <Reveal>
          <div className="rounded-[26px] bg-white border border-neutral-200 shadow-[0_35px_80px_-40px_rgba(15,23,42,0.35)] overflow-hidden" data-testid="compare-table">
            <div className="grid items-stretch" style={{ gridTemplateColumns: gridCols }} role="row">
              <div className="px-4 sm:px-6 py-4 sm:py-5" />
              {columns.map((c, i) => (
                <div key={i} className={`relative px-1.5 sm:px-3 pb-4 sm:pb-5 text-center text-[11px] sm:text-[13px] font-extrabold leading-tight flex flex-col items-center justify-end gap-1.5 ${i === lastIdx ? 'bg-slate-900 text-white pt-3 sm:pt-4 rounded-t-2xl' : 'text-slate-500 pt-4 sm:pt-5'}`} data-testid={`compare-col-${i}`}>
                  {i === lastIdx && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-400 text-slate-900 text-[9.5px] font-extrabold tracking-wider uppercase whitespace-nowrap shadow"><Crown size={10} />Best</span>
                  )}
                  <span>{c}</span>
                </div>
              ))}
            </div>
            {rows.map((r, ri) => (
              <div key={ri} className="grid items-center border-t border-neutral-100" style={{ gridTemplateColumns: gridCols }} data-testid={`compare-row-${ri}`}>
                <div className="px-4 sm:px-6 py-3.5 sm:py-4 text-[13.5px] sm:text-[15px] font-semibold text-slate-800 leading-snug">{r.label}</div>
                {columns.map((_, ci) => (
                  <div key={ci} className={`h-full px-1.5 sm:px-3 py-3.5 sm:py-4 flex items-center justify-center text-center ${ci === lastIdx ? 'bg-emerald-50/60' : ''}`}>
                    <Cell value={(r.values || [])[ci]} last={ci === lastIdx} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
